/**
 * Impact flashes: expanding shockwave rings, a flash sprite, and a burst of
 * pooled debris shards. All three come from fixed-size pools that are recycled,
 * so nothing allocates during a fight and the effect count is bounded by the
 * graphics preset.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { scene as bus, type ImpactView } from '../scene/sceneBus';
import { useSettings } from '../gameplay/settingsStore';
import { glowTexture } from '../assets/textures';

const POOL = 20;
const SHARDS_PER_IMPACT = 10;

interface Slot {
  impact: ImpactView | null;
  age: number;
  seed: number;
}

export function Impacts() {
  const maxImpacts = useSettings((s) => s.quality.maxImpacts);
  const ringRef = useRef<THREE.InstancedMesh>(null);
  const flashRef = useRef<THREE.InstancedMesh>(null);
  const shardRef = useRef<THREE.InstancedMesh>(null);

  const glow = useMemo(() => glowTexture(), []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);
  const slots = useRef<Slot[]>(
    Array.from({ length: POOL }, (_, i) => ({ impact: null, age: 0, seed: i * 0.618 })),
  );
  const consumed = useRef(new Set<number>());

  useFrame((state, delta) => {
    const ring = ringRef.current;
    const flash = flashRef.current;
    const shard = shardRef.current;
    if (!ring || !flash || !shard) return;

    // Claim newly-published impacts into free slots, oldest first.
    for (const impact of bus.impacts) {
      if (consumed.current.has(impact.id)) continue;
      const free = slots.current.find((s) => s.impact === null);
      if (!free) break;
      const active = slots.current.filter((s) => s.impact !== null).length;
      if (active >= maxImpacts) break;
      free.impact = impact;
      free.age = 0;
      consumed.current.add(impact.id);
    }
    if (consumed.current.size > 256) consumed.current.clear();
    if (bus.impacts.length > 0 && bus.impacts[0].bornAt < performance.now() / 1000 - 4) {
      bus.impacts.shift();
    }

    let shardIndex = 0;

    slots.current.forEach((slot, i) => {
      if (!slot.impact) {
        park(ring, i, dummy);
        park(flash, i, dummy);
        return;
      }
      slot.age += delta;
      const life = slot.impact.life;
      const p = slot.age / life;
      if (p >= 1) {
        slot.impact = null;
        park(ring, i, dummy);
        park(flash, i, dummy);
        return;
      }

      let { x, y, z } = slot.impact;
      color.set(slot.impact.color);
      let strength = Math.min(1.6, 0.5 + slot.impact.amount / 26);

      if (slot.impact.self) {
        // Push the burst out in front of the viewer and keep it small — the
        // screen flash and camera kick carry the impact, this is just a spark.
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(state.camera.quaternion);
        x = state.camera.position.x + forward.x * 2.4;
        y = state.camera.position.y + forward.y * 2.4;
        z = state.camera.position.z + forward.z * 2.4;
        strength = Math.min(0.55, strength * 0.35);
      }

      // The shockwave is billboarded at the viewer. Left lying flat it is seen
      // edge-on from a first-person stance and reads as a band across the
      // screen rather than as an impact.
      const radius = (0.4 + p * 3.4) * strength;
      dummy.position.set(x, y, z);
      dummy.quaternion.copy(state.camera.quaternion);
      dummy.rotateZ(slot.seed * 6.28);
      dummy.scale.set(radius, radius, radius);
      dummy.updateMatrix();
      ring.setMatrixAt(i, dummy.matrix);
      ring.setColorAt(i, color.clone().multiplyScalar(1 - p));

      // Flash.
      const flashScale = (1.6 - p * 1.2) * strength;
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.setScalar(Math.max(0.001, flashScale));
      dummy.updateMatrix();
      flash.setMatrixAt(i, dummy.matrix);
      flash.setColorAt(i, color.clone().multiplyScalar(Math.max(0, 1 - p * 1.5)));

      // Debris shards flying outward. Skipped for self-hits: shards spawned at
      // the camera would fly through the viewer's face.
      const shardCount = slot.impact.self ? 0 : SHARDS_PER_IMPACT;
      for (let s = 0; s < shardCount && shardIndex < POOL * SHARDS_PER_IMPACT; s++) {
        const a = (s / SHARDS_PER_IMPACT) * Math.PI * 2 + slot.seed * 3;
        const elev = Math.sin(s * 2.3 + slot.seed) * 0.8;
        const dist = p * 3.2 * strength;
        dummy.position.set(
          x + Math.cos(a) * dist,
          y + elev * dist * 0.5 - p * p * 1.6,
          z + Math.sin(a) * dist,
        );
        dummy.rotation.set(a + p * 6, elev * 4 + p * 5, p * 7);
        const sc = Math.max(0.001, (0.12 - p * 0.1) * strength);
        dummy.scale.setScalar(sc);
        dummy.updateMatrix();
        shard.setMatrixAt(shardIndex, dummy.matrix);
        shard.setColorAt(shardIndex, color.clone().multiplyScalar(Math.max(0, 1 - p)));
        shardIndex++;
      }
    });

    for (let i = shardIndex; i < POOL * SHARDS_PER_IMPACT; i++) park(shard, i, dummy);

    ring.instanceMatrix.needsUpdate = true;
    flash.instanceMatrix.needsUpdate = true;
    shard.instanceMatrix.needsUpdate = true;
    if (ring.instanceColor) ring.instanceColor.needsUpdate = true;
    if (flash.instanceColor) flash.instanceColor.needsUpdate = true;
    if (shard.instanceColor) shard.instanceColor.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={ringRef} args={[undefined, undefined, POOL]} frustumCulled={false}>
        <ringGeometry args={[0.72, 1, 40]} />
        <meshBasicMaterial
          transparent
          opacity={0.9}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>

      <instancedMesh ref={flashRef} args={[undefined, undefined, POOL]} frustumCulled={false}>
        <planeGeometry args={[1.6, 1.6]} />
        <meshBasicMaterial
          map={glow}
          transparent
          opacity={0.95}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>

      <instancedMesh ref={shardRef} args={[undefined, undefined, POOL * SHARDS_PER_IMPACT]} frustumCulled={false}>
        <tetrahedronGeometry args={[1, 0]} />
        <meshBasicMaterial
          transparent
          opacity={0.85}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>
    </group>
  );
}

function park(mesh: THREE.InstancedMesh, index: number, dummy: THREE.Object3D) {
  dummy.position.set(0, -1000, 0);
  dummy.rotation.set(0, 0, 0);
  dummy.scale.setScalar(0.0001);
  dummy.updateMatrix();
  mesh.setMatrixAt(index, dummy.matrix);
}
